import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import PaginatedList from '@theme/paginated-list';

/**
 * Search query parameter key
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Default search query for showing all products with filters
 * @type {string}
 */
const DEFAULT_SEARCH_QUERY = '*';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // Initialize default search if on search page without a query
    this.#initializeDefaultSearch();
  }

  /**
   * Checks if we're on a search page without a query and initializes
   * a default wildcard search to populate filters.
   *
   * This uses AJAX content loading (not a redirect) to remain WCAG 2.2.2 compliant.
   * The pattern matches how filter updates work in facets.js.
   *
   * @private
   * @returns {void}
   */
  #initializeDefaultSearch() {
    const url = new URL(window.location.href);
    const isSearchPage = url.pathname.includes('/search');
    const hasQuery = url.searchParams.has(SEARCH_QUERY) && url.searchParams.get(SEARCH_QUERY) !== '';

    // Only proceed if we're on a search page without a query
    if (!isSearchPage || hasQuery) return;

    // Wait for DOM to be ready before updating content
    // This ensures smooth content loading without layout shifts during initial parse
    requestIdleCallback(() => {
      this.#performDefaultSearch();
    });
  }

  /**
   * Performs the default wildcard search to populate filters.
   * Updates URL via history.replaceState and re-renders section via Section Rendering API.
   * Separated from initialization to allow for proper timing control.
   *
   * @private
   * @returns {void}
   */
  #performDefaultSearch() {
    const url = new URL(window.location.href);

    // Build the URL with default search parameters
    url.searchParams.set(SEARCH_QUERY, DEFAULT_SEARCH_QUERY);
    url.searchParams.set('type', 'product');

    // Update URL without navigation (WCAG-compliant - no redirect)
    history.replaceState({ defaultSearch: true }, '', url.toString());

    // Fetch and render the section with the search query to populate filters
    // This is the same pattern used by facets.js for filter updates
    const sectionId = this.getAttribute('section-id');
    if (sectionId) {
      startViewTransition(() => sectionRenderer.renderSection(sectionId, { url }), ['product-grid']);
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
